import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LoginLeft from '../../../components/LoginLeft';
import AuthForm from '../../../components/AuthForm';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * URGAA / Go4Garage — Production Login Screen
 * 
 * Layout: 
 * - Left: Brand panel with logo, metrics (2x2 grid), mascot
 * - Right: Auth form with MFA, real-time validation, caps lock indicator
 * 
 * Features:
 * - Responsive (mobile stacks vertically)
 * - WCAG AA accessible
 * - MFA handoff support
 * - Real-time validation
 * - Caps Lock indicator
 * - "Made in Bharat" branding
 * - No "Made with Emergent" badge
 */
const ProductionLogin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  return (
    <>
      {/* Page Head Configuration */}
      <Helmet>
        <title>Go4Garage | URGAA — Secure Login</title>
        <link rel="icon" href="/favicon-g4g.svg" type="image/svg+xml" />
        <meta name="description" content="Secure login to URGAA - India's First EV Infrastructure EcoSystem by Go4Garage" />
        <meta name="theme-color" content="#570683" />
      </Helmet>

      {/* Background Layer with Simple Gradient */}
      <div className="fixed inset-0 z-0 overflow-hidden" style={{
        background: 'linear-gradient(135deg, #F7F7FA 0%, #E8E8F0 100%)'
      }}>
        {/* Optional subtle pattern overlay */}
        <div 
          className="absolute inset-0 opacity-5" 
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #570683 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Main Grid Layout - No Scrolling, Everything Fits */}
      <main 
        className="relative z-10 flex h-screen overflow-hidden"
        style={{ 
          fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
        }}
      >
        {/* Left Panel - Brand & Live Energy Data */}
        <div className="flex-1 p-6 flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 to-white">
          <LoginLeft />
        </div>

        {/* Right Panel - Auth Form */}
        <div className="w-[450px] p-6 flex items-center justify-center overflow-hidden bg-white border-l border-gray-200">
          <AuthForm />
        </div>
      </main>

      {/* Global Styles for Motion */}
      <style jsx>{`
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </>
  );
};

export default ProductionLogin;
